"use client";

import { type ComponentType, type Key, type ReactNode } from "react";

import {
	InputText,
	Select,
	SelectItem,
	Text
} from "@pamperedchef/design-system";
import { regionsForCountry } from "@/domain/address";
import { formatPhoneNumber } from "@/composites/foundation/utilities/phone";

import * as styles from "./CreateListPanel.module.scss";

export type ShippingAddressCountry = "US" | "CA";

export interface ShippingAddressValues {
	firstName: string;
	lastName: string;
	phone: string;
	line1: string;
	line2: string;
	town: string;
	stateCode: string;
	postalCode: string;
}

export type ShippingAddressErrors = Partial<
	Record<keyof ShippingAddressValues, string>
>;

export interface ShippingAddressFieldsProps {
	country: ShippingAddressCountry;
	values: ShippingAddressValues;
	errors: ShippingAddressErrors;
	onChange: (patch: Partial<ShippingAddressValues>) => void;
	isDisabled?: boolean;
}

const RegionItem = SelectItem as unknown as ComponentType<{
	key?: Key;
	label?: ReactNode;
	textValue?: string;
	children?: ReactNode;
}>;

export const EMPTY_SHIPPING_ADDRESS: ShippingAddressValues = {
	firstName: "",
	lastName: "",
	phone: "",
	line1: "",
	line2: "",
	town: "",
	stateCode: "",
	postalCode: ""
};

const LABELS = {
	US: { region: "State", postalCode: "Zip Code" },
	CA: { region: "Province", postalCode: "Postal Code" }
} as const;

export function ShippingAddressFields({
	country,
	values,
	errors,
	onChange,
	isDisabled = false
}: ShippingAddressFieldsProps) {
	const labels = LABELS[country];
	const regions = regionsForCountry(country);

	return (
		<div
			className={styles["address"]}
			data-component="ShippingAddressFields"
		>
			<Text tag="p" styledAs="smr" className={styles["address__intro"]}>
				Where would you like your <strong>gifts</strong> to be{" "}
				<strong>sent</strong>? You can edit this information later if
				needed.
			</Text>

			<div className={styles["row"]}>
				<InputText
					label="First Name"
					name="shippingFirstName"
					autoComplete="shipping given-name"
					isRequired
					maxLength={25}
					value={values.firstName}
					onChange={(firstName) => onChange({ firstName })}
					isInvalid={!!errors.firstName}
					errorMessage={errors.firstName}
					isDisabled={isDisabled}
				/>
				<InputText
					label="Last Name"
					name="shippingLastName"
					autoComplete="shipping family-name"
					isRequired
					maxLength={25}
					value={values.lastName}
					onChange={(lastName) => onChange({ lastName })}
					isInvalid={!!errors.lastName}
					errorMessage={errors.lastName}
					isDisabled={isDisabled}
				/>
			</div>

			<InputText
				label="Phone Number"
				name="shippingPhone"
				type="tel"
				inputMode="tel"
				autoComplete="shipping tel"
				isRequired
				maxLength={14}
				value={values.phone}
				onChange={(phone) =>
					onChange({ phone: formatPhoneNumber(phone) })
				}
				isInvalid={!!errors.phone}
				errorMessage={errors.phone}
				isDisabled={isDisabled}
			/>

			<InputText
				label="Address Line 1"
				name="shippingLine1"
				autoComplete="shipping address-line1"
				isRequired
				value={values.line1}
				onChange={(line1) => onChange({ line1 })}
				isInvalid={!!errors.line1}
				errorMessage={errors.line1}
				isDisabled={isDisabled}
			/>

			<InputText
				label="Address Line 2 (Optional)"
				name="shippingLine2"
				autoComplete="shipping address-line2"
				value={values.line2}
				onChange={(line2) => onChange({ line2 })}
				isDisabled={isDisabled}
			/>

			<div className={styles["row"]}>
				<InputText
					label="City"
					name="shippingTown"
					autoComplete="shipping address-level2"
					isRequired
					value={values.town}
					onChange={(town) => onChange({ town })}
					isInvalid={!!errors.town}
					errorMessage={errors.town}
					isDisabled={isDisabled}
				/>
				<div className={styles["field"]}>
					<Select
						label={labels.region}
						name="shippingStateCode"
						isRequired
						placeholderText="Select"
						selectedKey={values.stateCode || null}
						onSelectionChange={(key) =>
							onChange({ stateCode: String(key ?? "") })
						}
						errorMessage={errors.stateCode}
						isDisabled={isDisabled}
					>
						{regions.map((region) => (
							<RegionItem
								key={region.code}
								label={region.name}
								textValue={region.name}
							>
								{region.name}
							</RegionItem>
						))}
					</Select>
				</div>
			</div>

			<div className={styles["row"]}>
				<InputText
					label={labels.postalCode}
					name="shippingPostalCode"
					autoComplete="shipping postal-code"
					isRequired
					value={values.postalCode}
					onChange={(postalCode) => onChange({ postalCode })}
					isInvalid={!!errors.postalCode}
					errorMessage={errors.postalCode}
					isDisabled={isDisabled}
				/>
			</div>
		</div>
	);
}
